/**
 * Work / policy / calendar jurisdiction (LPH-T35): country = ISO 3166-1 alpha-2 (`CA`), state = the FULL ISO 3166-2 code
 * (`CA-ON`) whose first two letters are the country. Mirrors the BE rule (time-tracking `TT_INVALID_JURISDICTION_CODE`,
 * entity-management `EM_WORKER_JURISDICTION_INVALID`, both with `extensions.data.violation`); the server stays
 * authoritative. Shared by the leave-policy, holiday-calendar and work-location forms.
 */

export type JurisdictionViolation = 'COUNTRY_FORMAT' | 'STATE_FORMAT' | 'STATE_WITHOUT_COUNTRY' | 'STATE_PREFIX_MISMATCH'

const COUNTRY = /^[A-Z]{2}$/
const STATE = /^[A-Z]{2}-[A-Z0-9]{1,3}$/

/** Trim + upper-case, the same normalisation the server applies before validating (country and state codes alike). */
export function normaliseStateCode(raw: string): string {
  return raw.trim().toUpperCase()
}

/**
 * The first rule a (country, state) pair breaks, or `null`. Both are normalised first; an empty country with an empty state
 * is valid (no jurisdiction).
 */
export function validateState(countryRaw: string, stateRaw: string): JurisdictionViolation | null {
  const country = normaliseStateCode(countryRaw)
  const state = normaliseStateCode(stateRaw)
  if (country && !COUNTRY.test(country)) return 'COUNTRY_FORMAT'
  if (!state) return null
  if (!country) return 'STATE_WITHOUT_COUNTRY'
  if (!STATE.test(state)) return 'STATE_FORMAT'
  if (state.slice(0, 2) !== country) return 'STATE_PREFIX_MISMATCH'
  return null
}

/** Copy per violation (PRD lph-t35-fe-hierarchy FR-3). */
export const STATE_VIOLATION_COPY: Record<JurisdictionViolation, string> = {
  COUNTRY_FORMAT: 'Enter a valid 2-letter country code, for example CA.',
  STATE_FORMAT: 'Enter the full state code, for example CA-ON.',
  STATE_WITHOUT_COUNTRY: 'Choose a country before a state.',
  STATE_PREFIX_MISMATCH: 'The state code must start with the country code.',
}

/** Narrow an untyped `extensions.data.violation` to a known violation, else `null`. */
export function asJurisdictionViolation(value: unknown): JurisdictionViolation | null {
  return typeof value === 'string' && Object.hasOwn(STATE_VIOLATION_COPY, value) ? (value as JurisdictionViolation) : null
}
